import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import DashboardHeader from '../../components/common/DashboardHeader';
import {
  CreditCardIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  DocumentTextIcon,
  CalendarIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface Subscription {
  id: number;
  garage_id: number;
  garage?: { name: string; id: number };
  plan_type: string;
  amount: number;
  referral_discount: number;
  start_date: string;
  end_date: string;
  status: 'active' | 'expired' | 'cancelled';
  payment_date?: string;
  days_until_expiry?: number;
  created_at: string;
}

interface SubscriptionStats {
  by_status: Record<string, number>;
  by_plan: Record<string, number>;
  total_revenue: number;
  monthly_revenue: number;
  expiring_7_days: number;
  expiring_30_days: number;
  average_duration_days: number;
}

const SubscriptionManagement: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionStats, setSubscriptionStats] = useState<SubscriptionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [garageFilter, setGarageFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expiringSubscriptions, setExpiringSubscriptions] = useState<Subscription[]>([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<number[]>([]);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

  useEffect(() => {
    fetchSubscriptions();
    fetchSubscriptionStats();
    fetchExpiringSubscriptions();
  }, [page, statusFilter, planFilter, garageFilter, search]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/subscriptions', {
        params: {
          page,
          limit: 20,
          status: statusFilter,
          plan_type: planFilter,
          garage_id: garageFilter,
          search,
        },
      });
      setSubscriptions(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStats = async () => {
    try {
      const response = await api.get('/admin/subscriptions/stats');
      setSubscriptionStats(response.data.data);
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
    }
  };

  const fetchExpiringSubscriptions = async () => {
    try {
      const response = await api.get('/admin/subscriptions/expiring', {
        params: { days: 7 },
      });
      setExpiringSubscriptions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching expiring subscriptions:', error);
    }
  };

  const cancelSubscription = async (subscriptionId: number, reason: string, immediate: boolean = false) => {
    try {
      await api.post(`/admin/subscriptions/${subscriptionId}/cancel`, {
        reason,
        immediate
      });
      toast.success('Subscription cancelled successfully');
      fetchSubscriptions();
      fetchSubscriptionStats();
      fetchExpiringSubscriptions();
      setShowCancelModal(false);
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Failed to cancel subscription';
      toast.error(errorMessage);
    }
  };

  const extendSubscription = async (subscriptionId: number, days: number, reason: string) => {
    try {
      await api.post(`/admin/subscriptions/${subscriptionId}/extend`, {
        days,
        reason
      });
      toast.success(`Subscription extended by ${days} days`);
      fetchSubscriptions();
      fetchSubscriptionStats();
      setShowExtendModal(false);
    } catch (error: any) {
      console.error('Error extending subscription:', error);
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Failed to extend subscription';
      toast.error(errorMessage);
    }
  };

  const reactivateSubscription = async (subscriptionId: number, endDate: string) => {
    try {
      await api.post(`/admin/subscriptions/${subscriptionId}/reactivate`, {
        end_date: endDate
      });
      toast.success('Subscription reactivated successfully');
      fetchSubscriptions();
      fetchSubscriptionStats();
      setShowReactivateModal(false);
    } catch (error: any) {
      console.error('Error reactivating subscription:', error);
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Failed to reactivate subscription';
      toast.error(errorMessage);
    }
  };

  const bulkUpdateSubscriptions = async (operation: string, params: any = {}) => {
    if (selectedSubscriptions.length === 0) {
      toast.error('Please select subscriptions first');
      return;
    }

    try {
      const response = await api.post('/admin/subscriptions/bulk-update', {
        subscription_ids: selectedSubscriptions,
        operation,
        params
      });
      
      const { success, failed } = response.data;
      toast.success(`Bulk operation completed: ${success} successful, ${failed} failed`);
      setSelectedSubscriptions([]);
      fetchSubscriptions();
      fetchSubscriptionStats();
    } catch (error: any) {
      console.error('Error performing bulk operation:', error);
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Failed to perform bulk operation';
      toast.error(errorMessage);
    }
  };

  const handleExtendClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowExtendModal(true);
  };

  const handleCancelClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowCancelModal(true);
  };

  const handleReactivateClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowReactivateModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'expired':
        return <XCircleIcon className="w-4 h-4" />;
      case 'cancelled':
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'currency',
      currency: 'KWD',
    }).format(amount);
  };

  const formatPlanType = (planType: string) => {
    return planType === 'monthly' ? 'Monthly' : planType === 'semi_annual' ? '6-Month' : planType;
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <CreditCardIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B] mr-2 sm:mr-3" />
                Subscription Management
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage all platform subscriptions and renewals</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => bulkUpdateSubscriptions('extend', { days: 30, reason: 'Bulk extension by admin' })}
                className="bg-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-green-700 flex items-center justify-center text-sm sm:text-base"
                disabled={selectedSubscriptions.length === 0}
              >
                <CalendarIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                <span className="hidden sm:inline">Bulk Extend Selected</span>
                <span className="sm:hidden">Bulk Extend</span>
              </button>
              <button
                onClick={() => bulkUpdateSubscriptions('cancel', { reason: 'Bulk cancellation by admin', immediate: false })}
                className="bg-red-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-red-700 flex items-center justify-center text-sm sm:text-base"
                disabled={selectedSubscriptions.length === 0}
              >
                <XCircleIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                <span className="hidden sm:inline">Bulk Cancel Selected</span>
                <span className="sm:hidden">Bulk Cancel</span>
              </button>
            </div>
          </div>
        </div>

        {/* Expiring Subscriptions Alert */}
        {expiringSubscriptions.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 sm:w-6 h-5 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-medium text-red-800 mb-2 sm:mb-3">
                  {expiringSubscriptions.length} Subscriptions Expiring Within 7 Days
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3">
                  {expiringSubscriptions.slice(0, 6).map((sub) => (
                    <div key={sub.id} className="flex items-center justify-between bg-white rounded-lg p-2 sm:p-3">
                      <div className="min-w-0 flex-1 mr-2">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{sub.garage?.name}</p>
                        <p className="text-xs text-red-600">
                          Expires in {sub.days_until_expiry || 0} days
                        </p>
                      </div>
                      <button
                        onClick={() => handleExtendClick(sub)}
                        className="text-xs bg-red-600 text-white px-2 sm:px-3 py-1 rounded hover:bg-red-700 whitespace-nowrap"
                      >
                        Extend
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="w-6 sm:w-8 h-6 sm:h-8 text-green-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Active</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {subscriptionStats?.by_status?.active || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <XCircleIcon className="w-6 sm:w-8 h-6 sm:h-8 text-red-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Expired</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {subscriptionStats?.by_status?.expired || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-6 sm:w-8 h-6 sm:h-8 text-yellow-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Expiring Soon</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {subscriptionStats?.expiring_7_days || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <BanknotesIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B] mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Monthly Revenue</p>
                <p className="text-base sm:text-xl lg:text-2xl font-bold text-gray-900">
                  {formatCurrency(subscriptionStats?.monthly_revenue || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by garage name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 sm:pl-10 w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
            >
              <option value="">All Plans</option>
              <option value="monthly">Monthly</option>
              <option value="semi_annual">6-Month</option>
            </select>
            <input
              type="text"
              placeholder="Garage ID"
              value={garageFilter}
              onChange={(e) => setGarageFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
            />
            <div className="text-center lg:text-right">
              <span className="text-xs sm:text-sm text-gray-600">
                Total: <span className="font-bold text-gray-900">{total}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Subscriptions Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSubscriptions(subscriptions.map(s => s.id));
                        } else {
                          setSelectedSubscriptions([]);
                        }
                      }}
                      checked={selectedSubscriptions.length === subscriptions.length && subscriptions.length > 0}
                      className="rounded border-gray-300 text-[#008B8B] focus:ring-[#008B8B]"
                    />
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Garage
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan & Amount
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Date
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008B8B] mx-auto"></div>
                    </td>
                  </tr>
                ) : subscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      No subscriptions found
                    </td>
                  </tr>
                ) : (
                  subscriptions.map((subscription) => (
                    <tr key={subscription.id} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-6 py-2 sm:py-4">
                        <input
                          type="checkbox"
                          checked={selectedSubscriptions.includes(subscription.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSubscriptions([...selectedSubscriptions, subscription.id]);
                            } else {
                              setSelectedSubscriptions(selectedSubscriptions.filter(id => id !== subscription.id));
                            }
                          }}
                          className="rounded border-gray-300 text-[#008B8B] focus:ring-[#008B8B]"
                        />
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <div className="text-xs sm:text-sm font-medium text-gray-900">
                          {subscription.garage?.name || 'Unknown Garage'}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500">ID: {subscription.garage_id}</div>
                        <div className="sm:hidden text-xs text-gray-500 mt-1">
                          {formatPlanType(subscription.plan_type)} - {formatCurrency(subscription.amount - subscription.referral_discount)}
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatPlanType(subscription.plan_type)}</div>
                        <div className="text-sm text-gray-500">
                          {formatCurrency(subscription.amount)}
                          {subscription.referral_discount > 0 && (
                            <span className="text-green-600"> (-{formatCurrency(subscription.referral_discount)})</span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <span className={`px-1 sm:px-2 py-0.5 sm:py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getStatusColor(subscription.status)}`}>
                          <span className="hidden sm:inline">{getStatusIcon(subscription.status)}</span>
                          <span className="sm:ml-1">{getStatusText(subscription.status)}</span>
                        </span>
                        {subscription.status === 'active' && subscription.days_until_expiry !== undefined && subscription.days_until_expiry <= 7 && (
                          <div className="text-xs text-red-600 mt-1 sm:hidden">
                            {subscription.days_until_expiry}d left
                          </div>
                        )}
                      </td>
                      <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(subscription.start_date).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          to {new Date(subscription.end_date).toLocaleDateString()}
                        </div>
                        {subscription.status === 'active' && subscription.days_until_expiry !== undefined && subscription.days_until_expiry <= 7 && (
                          <div className="text-xs text-red-600 mt-1">
                            Expires in {subscription.days_until_expiry} days
                          </div>
                        )}
                      </td>
                      <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {subscription.payment_date ? new Date(subscription.payment_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-1 sm:space-x-2">
                          {subscription.status === 'active' && (
                            <button
                              onClick={() => handleExtendClick(subscription)}
                              className="text-green-600 hover:text-green-900"
                              title="Extend"
                            >
                              <CalendarIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                            </button>
                          )}
                          {(subscription.status === 'expired' || subscription.status === 'cancelled') && (
                            <button
                              onClick={() => handleReactivateClick(subscription)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Reactivate"
                            >
                              <ArrowPathIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                            </button>
                          )}
                          {subscription.status === 'active' && (
                            <button
                              onClick={() => handleCancelClick(subscription)}
                              className="text-red-600 hover:text-red-900"
                              title="Cancel"
                            >
                              <XCircleIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {total > 20 && (
            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * 20 >= total}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(page - 1) * 20 + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(page * 20, total)}</span> of{' '}
                    <span className="font-medium">{total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= total}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Extend Modal */}
        {showExtendModal && selectedSubscription && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Extend Subscription</h3>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);
                    const days = parseInt(formData.get('days') as string);
                    const reason = formData.get('reason') as string;
                    extendSubscription(selectedSubscription.id, days, reason);
                  }}
                >
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Garage: {selectedSubscription.garage?.name}
                    </label>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Extension Days
                    </label>
                    <input
                      type="number"
                      name="days"
                      min="1"
                      max="365"
                      defaultValue="30"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason
                    </label>
                    <textarea
                      name="reason"
                      required
                      placeholder="Enter reason for extension..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowExtendModal(false)}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666]"
                    >
                      Extend
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Modal */}
        {showCancelModal && selectedSubscription && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Cancel Subscription</h3>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);
                    const reason = formData.get('reason') as string;
                    const immediate = formData.get('immediate') === 'on';
                    cancelSubscription(selectedSubscription.id, reason, immediate);
                  }}
                >
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Garage: {selectedSubscription.garage?.name}
                    </label>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cancellation Reason
                    </label>
                    <textarea
                      name="reason"
                      required
                      placeholder="Enter reason for cancellation..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                      rows={3}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="immediate"
                        className="rounded border-gray-300 text-[#008B8B] focus:ring-[#008B8B]"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Cancel immediately (otherwise cancels at end of period)
                      </span>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowCancelModal(false)}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Cancel Subscription
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Reactivate Modal */}
        {showReactivateModal && selectedSubscription && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reactivate Subscription</h3>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);
                    const endDate = formData.get('end_date') as string;
                    reactivateSubscription(selectedSubscription.id, endDate);
                  }}
                >
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Garage: {selectedSubscription.garage?.name}
                    </label>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New End Date
                    </label>
                    <input
                      type="date"
                      name="end_date"
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowReactivateModal(false)}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666]"
                    >
                      Reactivate
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SubscriptionManagement;